import { DIGITAL_SOURCE_TYPE, embedDigitalSourceType } from '@backend/lib/util/aiProvenanceXmp'
import { requiresAiDisclosure, type WorkspaceMedia } from '@backend/lib/db/typeRoots/workspaceMedia'

/**
 * Re-stamps a flattened canvas PNG with the IPTC "composite contains AI" marker
 * (`compositeWithTrainedAlgorithmicMedia`) when the canvas is AI-generated — see
 * GH #118.
 *
 * Canvas flattening happens in the browser (`renderCanvasOffscreen` / the canvas
 * editor), so any provenance metadata the source AI images carried is lost when
 * they're rasterised into a single PNG. This re-applies the marker to the final
 * file just before upload, gated on the canvas's own provenance.
 *
 * Best-effort: returns the original file unchanged when no disclosure is
 * required, when the image is already marked, or on any failure.
 */
export async function stampCanvasProvenance(
  file: File,
  provenance: Pick<WorkspaceMedia, 'origin' | 'hasSyntheticEdits'>,
): Promise<File> {
  if (!requiresAiDisclosure(provenance)) return file
  try {
    const bytes = new Uint8Array(await file.arrayBuffer())
    const stamped = embedDigitalSourceType(bytes, DIGITAL_SOURCE_TYPE.composite)
    if (stamped === bytes) return file // already marked — no-op
    // Copy into a fresh ArrayBuffer-backed view so the bytes satisfy BlobPart
    // (the shared core types its output as the wider Uint8Array<ArrayBufferLike>).
    const exact = new Uint8Array(stamped.length)
    exact.set(stamped)
    return new File([exact], file.name, { type: file.type })
  } catch (error) {
    console.warn('[canvasProvenance] Failed to stamp AI provenance on canvas export', error)
    return file
  }
}
